import { useState } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { teacherPriorityApi, type PrepResponse } from '@/services/api/teacherPriority'
import { LessonPrepStatusResponse } from '@/types/teacher-request'
import { PrepLessonList } from '@/components/teacher-request/PrepLessonList'
import { PrepEditor } from '@/components/teacher-request/PrepEditor'
import { PrepChatPanel } from '@/components/teacher-request/PrepChatPanel'
import { useAuthStore } from '@/store'
import {
  getPrepBackPath,
  getPrepListLabel,
  parsePrepOrigin,
  resolvePrepOrigin,
  type PrepLocationState,
} from '@/utils/prepNavigation'
import { isChatReadOnlyStatus } from '@/utils/chatHelpers'

export function TeacherRequestPrepPage() {
  const { id: requestId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedPrepId, setSelectedPrepId] = useState<string | null>(null)
  const [selectedLessonId, setSelectedLessonId] = useState<string | undefined>()
  const user = useAuthStore((state) => state.user)

  const prepLiveRefetch = () => (document.hidden ? false : 10000)

  const { data: preps = [], isLoading: prepsLoading, refetch: refetchPreps } = useQuery<PrepResponse[]>({
    queryKey: ['preps', requestId],
    queryFn: () => requestId ? teacherPriorityApi.getPreps(requestId) : Promise.resolve([]),
    enabled: !!requestId,
    refetchInterval: prepLiveRefetch,
    refetchOnWindowFocus: true,
  })

  const { data: lessonStatus = [], isLoading: statusLoading } = useQuery<LessonPrepStatusResponse[]>({
    queryKey: ['lesson-status', requestId],
    queryFn: () => requestId ? teacherPriorityApi.getLessonPrepStatus(requestId) : Promise.resolve([]),
    enabled: !!requestId,
    refetchInterval: prepLiveRefetch,
    refetchOnWindowFocus: true,
  })

  const { data: requestStatus } = useQuery({
    queryKey: ['request-status', requestId],
    queryFn: () => requestId ? teacherPriorityApi.getRequestStatus(requestId) : Promise.resolve(null),
    enabled: !!requestId,
    refetchInterval: prepLiveRefetch,
    refetchOnWindowFocus: true,
  })

  const handleLessonClick = (lessonId: string) => {
    setSelectedLessonId(lessonId)
    const lesson = lessonStatus.find(l => l.lesson_request_id === lessonId)
    if (lesson?.prep_id) {
      setSelectedPrepId(lesson.prep_id)
    }
  }

  if (!requestId) return null

  const originFromNav = parsePrepOrigin((location.state as PrepLocationState | null)?.from)
    ?? parsePrepOrigin(new URLSearchParams(location.search).get('from'))
  const backOrigin = resolvePrepOrigin({
    origin: originFromNav,
    currentTeacherId: user?.teacher_id,
    requestingTeacherId: requestStatus?.requesting_teacher_id,
  })
  const backPath = getPrepBackPath({
    requestId,
    origin: backOrigin,
  })
  const listLabel = getPrepListLabel(backOrigin)

  const isLoading = prepsLoading || statusLoading

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Wird geladen...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4">
        <div className="text-sm text-gray-500 mb-3">
          <Link to={backPath} className="text-blue-600 hover:text-blue-800">{listLabel}</Link>
          {' / '}
          <Link to={backPath} className="text-blue-600 hover:text-blue-800">
            {requestStatus ? `${requestStatus.status} ${requestStatus.request_id?.slice(0, 8)}` : requestId.slice(0, 8)}
          </Link>
          {' / '}
          <span className="font-medium text-gray-700">Vorbereitung (Prep)</span>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Vorbereitung (Prep)</h2>
              <p className="text-sm text-gray-500 mt-1">
                {lessonStatus.length} Lektionen · {preps.length} Preps
              </p>
            </div>
            <button
              onClick={() => navigate(backPath)}
              className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
            >
              ← Zurück zur Anfrage
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4" style={{ height: 'calc(100vh - 220px)' }}>
          <div className="col-span-3">
            <PrepLessonList
              requestId={requestId}
              lessonStatus={lessonStatus}
              selectedLessonId={selectedLessonId}
              onLessonClick={handleLessonClick}
            />
          </div>
          <div className="col-span-5">
            <PrepEditor
              requestId={requestId}
              preps={preps}
              lessonStatus={lessonStatus}
              selectedPrepId={selectedPrepId}
              onPrepChange={setSelectedPrepId}
              onSaved={() => refetchPreps()}
            />
          </div>
          <div className="col-span-4">
            <PrepChatPanel 
              requestId={requestId} 
              isReadOnly={isChatReadOnlyStatus(requestStatus?.status)}
              requestingTeacherId={requestStatus?.requesting_teacher_id}
              currentTeacherId={user?.teacher_id}
              substituteTeacherId={requestStatus?.substitute_teacher_id}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
